import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Title } from "@angular/platform-browser";

import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import * as Waves from "node-waves";

import { CoreMenuService } from "@core/components/core-menu/core-menu.service";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreTranslationService } from "@core/services/translation.service";

import { collaboratorMenu, menu } from "app/menu/menu";

import { locale as italian } from "app/i18n/it";
import { locale as english } from "app/i18n/en";
import { locale as french } from "app/i18n/fr";
import { locale as german } from "app/i18n/de";
import { locale as portuguese } from "app/i18n/pt";

import { AuthenticationService, UserService } from "./auth/service";
import { CitiesService } from "./auth/service/cities.service";
import { RoleService } from "./auth/service/role.service";
import { UserFigureService } from "./auth/service/user-figure.service";
import { ContractService } from "./auth/service/contract.service";
import { CertificationService } from "./auth/service/certification.service";
import { UtilsService } from "./auth/service/utils.service";
import { JobSiteService } from "./auth/service/job-sites.service";
import { TutorialService } from "./auth/service/tutorial.service";
import { AbsencesService } from "./auth/service/absences.service";
import { TicketService } from "./modules/ticket/ticket.service";
import { CustomerService } from "./modules/customer/customer.service";
import UserOptionsService from "./auth/service/user-options.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  coreConfig: any;

  menu: any;

  defaultLanguage: "it"; // This language will be used as a fallback when a translation isn't found in the current language

  appLanguage: "it"; // Set application default language i.e fr

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {DOCUMENT} document
   * @param {Title} _title
   * @param {Renderer2} _renderer
   * @param {ElementRef} _elementRef
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMenuService} _coreMenuService
   * @param {CoreTranslationService} _coreTranslationService
   * @param {TranslateService} _translateService
   * @param _authenticationService
   * @param _citiesService
   * @param _userFigureService
   * @param _contractService
   * @param _roleService
   * @param _certificationService
   * @param _utilService
   * @param _customerService
   * @param _jobSiteService
   * @param _tutorialService
   * @param _userService
   * @param _absencesService
   * @param _ticketService
   * @param userOptionsService
   */

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    public _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreMenuService: CoreMenuService,
    private _coreTranslationService: CoreTranslationService,
    private _translateService: TranslateService,
    private _authenticationService: AuthenticationService,
    private _citiesService: CitiesService,
    private _userFigureService: UserFigureService,
    private _contractService: ContractService,
    private _roleService: RoleService,
    private _certificationService: CertificationService,
    public _utilService: UtilsService,
    private _customerService: CustomerService,
    private _jobSiteService: JobSiteService,
    public _tutorialService: TutorialService,
    public _userService: UserService,
    public _absencesService: AbsencesService,
    private _ticketService: TicketService,
    private userOptionsService: UserOptionsService,
  ) {
    this._authenticationService.currentUser.subscribe((user: any) => {
      if(user) {
        // Get the application main menu
        if (user.type === "Dipendente") {
          this.menu = menu;
        } else if (["Stagista", "Partita Iva", "Da Fornitore", "Prestazione Occasionale Archiviato", "Prestazione Occasionale", "Stagista Archiviato", "Partita Iva Archiviato", "Fornitori"].includes(user.type)) {
          this.menu = collaboratorMenu;
        }
        // Register the menu to the menu service
        this._coreMenuService.register("main", this.menu);

        // Set the main menu as our current menu
        this._coreMenuService.setCurrentMenu("main");
      }
    })

    // Add languages to the translation service
    this._translateService.addLangs(["en", "fr", "de", "pt"]);

    // This language will be used as a fallback when a translation isn't found in the current language
    this._translateService.setDefaultLang("en");

    // Set the translations for all other items
    this._coreTranslationService.translate(english, french, german, portuguese, italian);

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  async ngOnInit() {
    // Load core configuration
    this._coreConfigService.getConfig().subscribe((data: any) => {
      // Import languages from custom configuration
      for (const locale of data.custom.languages) {
        this._translateService.setTranslation(locale.lang, locale.data, true);
      }
      // Add custom menu voices and reorder menu items
      for (const m of data.custom.menu) {
        // controllo che non sia già presente per evitare duplicati
        if (!this.menu.find((menuitem: any) => menuitem.id === m.id)) {
          this.menu.push(m);
        }
      }

      this.menu = this.menu.sort((a: any, b: any) => {
        a = Number(a.order || 1);
        b = Number(b.order || 1);
        return a > b ? 1 : a < b ? -1 : 0;
      });
    });
    // Init wave effect (Ripple effect)
    Waves.init();
    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;

      // Set application default language.

      // Change application language? Read the ngxTranslate Fix

      // ? Use app-config.ts file to set default language
      const appLanguage = this.coreConfig.app.appLanguage || "it";
      this._translateService.use(appLanguage);

      // ? OR
      // ? User the current browser lang if available, if undefined use 'en'
      // const browserLang = this._translateService.getBrowserLang();
      // this._translateService.use(browserLang.match(/en|fr|de|pt/) ? browserLang : 'en');

      /**
       * ! Fix : ngxTranslate
       * ----------------------------------------------------------------------------------------------------
       */

      /**
       *
       * Using different language than the default ('en') one i.e French?
       * In this case, you may find the issue where application is not properly translated when your app is initialized.
       *
       * It's due to ngxTranslate module and below is a fix for that.
       * Eventually we will move to the multi language implementation over to the Angular's core language service.
       *
       **/

      // Set the default language to 'en' and then back to 'fr'.

      setTimeout(() => {
        this._translateService.setDefaultLang("it");
        this._translateService.setDefaultLang(appLanguage);
      });

      /**
       * !Fix: ngxTranslate
       * !Fix: ngxTranslate
       * ----------------------------------------------------------------------------------------------------
       */

      // Layout
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove("vertical-layout", "vertical-menu-modern", "horizontal-layout", "horizontal-menu");
      // Add class based on config options
      if (this.coreConfig.layout.type === "vertical") {
        this._elementRef.nativeElement.classList.add("vertical-layout", "vertical-menu-modern");
      } else if (this.coreConfig.layout.type === "horizontal") {
        this._elementRef.nativeElement.classList.add("horizontal-layout", "horizontal-menu");
      }

      // Navbar
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove("navbar-floating", "navbar-static", "navbar-sticky", "navbar-hidden");

      // Add class based on config options
      if (this.coreConfig.layout.navbar.type === "navbar-static-top") {
        this._elementRef.nativeElement.classList.add("navbar-static");
      } else if (this.coreConfig.layout.navbar.type === "fixed-top") {
        this._elementRef.nativeElement.classList.add("navbar-sticky");
      } else if (this.coreConfig.layout.navbar.type === "floating-nav") {
        this._elementRef.nativeElement.classList.add("navbar-floating");
      } else {
        this._elementRef.nativeElement.classList.add("navbar-hidden");
      }

      // Footer
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove("footer-fixed", "footer-static", "footer-hidden");

      // Add class based on config options
      if (this.coreConfig.layout.footer.type === "footer-sticky") {
        this._elementRef.nativeElement.classList.add("footer-fixed");
      } else if (this.coreConfig.layout.footer.type === "footer-static") {
        this._elementRef.nativeElement.classList.add("footer-static");
      } else {
        this._elementRef.nativeElement.classList.add("footer-hidden");
      }

      // Blank layout
      if (this.coreConfig.layout.menu.hidden && this.coreConfig.layout.navbar.hidden && this.coreConfig.layout.footer.hidden) {
        this._elementRef.nativeElement.classList.add("blank-page");
        // ! Fix: Transition issue while coming from blank page
        this._renderer.setAttribute(this._elementRef.nativeElement.getElementsByClassName("app-content")[0], "style", "transition:none");
      } else {
        this._elementRef.nativeElement.classList.remove("blank-page");
        // ! Fix: Transition issue while coming from blank page
        setTimeout(() => {
          this._renderer.setAttribute(this._elementRef.nativeElement.getElementsByClassName("app-content")[0], "style", "transition:300ms ease all");
        }, 0);
        // If navbar hidden
        if (this.coreConfig.layout.navbar.hidden) {
          this._elementRef.nativeElement.classList.add("navbar-hidden");
        }
        // Menu (Vertical menu hidden)
        if (this.coreConfig.layout.menu.hidden) {
          this._renderer.setAttribute(this._elementRef.nativeElement, "data-col", "1-column");
        } else {
          this._renderer.removeAttribute(this._elementRef.nativeElement, "data-col");
        }
        // Footer
        if (this.coreConfig.layout.footer.hidden) {
          this._elementRef.nativeElement.classList.add("footer-hidden");
        }
      }

      // Skin Class (Adding to body as it requires highest priority)
      if (this.coreConfig.layout.skin !== "" && this.coreConfig.layout.skin !== undefined) {
        this.document.body.classList.remove("default-layout", "bordered-layout", "dark-layout", "semi-dark-layout");
        this.document.body.classList.add(this.coreConfig.layout.skin + "-layout");
      }
    });

    // Set the application page title
    this._title.setTitle(this.coreConfig.app.appTitle);

    // se loggato carichiamo alcune liste
    const currentUser: any = this._authenticationService.currentUserValue;
    if (currentUser && currentUser.token) {
      this._utilService.loadOptions();
      //Mi carico all'inizio alcune liste importanti
      await this._userService.getAllUsers();
      await this._userFigureService.getList();
      await this._citiesService.getList();
      await this._roleService.getList();
      await this._contractService.getList();
      await this._contractService.getPrograms();
      await this._certificationService.getList();
      await this._jobSiteService.getList();

      if (currentUser.capabilities.includes("show_customers_list")) {
        await this._customerService.getCustomers();
      }
      if (currentUser.capabilities.includes("show_customer_addresses_list")) {
        await this._customerService.getAddresses();
      }
      if (currentUser.capabilities.includes("show_customer_referents_list")) {
        await this._customerService.getReferents();
      }

      // carico le opzioni dell'utente
      this.userOptionsService.list().subscribe();
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  closeModal() {
    this._utilService.popupOpenTaskIsOpen = false;
  }


  async updateMyAbsences() {
    this._absencesService.myAbsences = await this._absencesService.getMineAbsences();
  }
  async updateTicketList() {
    this._ticketService.mineTicket = await this._ticketService.getMineTicket();
  }
}
